import { DeleteButton, SaveButton } from '@/components/_common';
import {
  useCreateInvoiceCarrier,
  useInvoiceCarrier,
  useRemoveInvoiceCarrier,
  useUpdateInvoiceCarrier,
} from '@/core/domain/invoice/invoiceCarrier/invoiceCarrier.hooks';
import { InvoiceCarrierRequest } from '@/core/domain/invoice/invoiceCarrier/invoiceCarrier.types';
import { applyMask, removeMask } from '@/utils/mask';
import { cepMask, cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { Button, Form, Modal, Space } from 'antd';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import InvoiceTransportForm from './InvoiceTransportForm';

interface Props {
  visible: boolean;
  toggleModal: () => void;
  carrierId?: number;
  resetCarrierId: () => void;
  resetList?: () => void;
}

export default function InvoiceTransportCreateModal({
  visible,
  toggleModal,
  carrierId,
  resetCarrierId,
  resetList,
}: Props) {
  const [form] = Form.useForm<InvoiceCarrierRequest>();
  const createMutation = useCreateInvoiceCarrier();
  const removeMutation = useRemoveInvoiceCarrier();
  const updateMutation = useUpdateInvoiceCarrier();
  const invoiceCarrier = useInvoiceCarrier(carrierId);

  useEffect(() => {
    const data = invoiceCarrier.data;

    if (data && carrierId) {
      form.setFieldsValue({
        name: data.name,
        address: {
          ...data.address,
          zip: data.address?.zip
            ? applyMask(removeMask(data.address.zip), cepMask)
            : undefined,
        },
        cpfCnpj:
          data.cpfCnpj.length <= 11
            ? applyMask(data.cpfCnpj, cpfMask)
            : applyMask(data.cpfCnpj, cnpjMask),
        personType: data.personType,
        stateRegistration: data.stateRegistration,
        observation: data.observation,
        active: data.active,
      });
    }
  }, [invoiceCarrier.dataUpdatedAt, visible]);

  const handleOnSave = async () => {
    try {
      const values = await form.validateFields();
      carrierId && invoiceCarrier.data
        ? await updateMutation.mutateAsync({
            ...values,
            address: { ...values.address, zip: removeMask(values.address.zip) },
            cpfCnpj: removeMask(values.cpfCnpj),
            active: true,
            id: carrierId,
          })
        : await createMutation.mutateAsync(values);
      handleCloseModal();
      resetCarrierId();
    } catch {}
  };

  const handleRemove = async () => {
    if (carrierId) {
      try {
        await removeMutation.mutateAsync(carrierId);
        resetCarrierId();
        handleCloseModal();
        if (!!resetList) {
          resetList();
        }
      } catch {}
    }
  };

  const handleCloseModal = () => {
    form.resetFields();
    resetCarrierId();
    toggleModal();
  };

  const intl = (message: string) => useIntl().formatMessage({ id: message });
  const translate = (message: string, type: string) =>
    intl(`invoiceTransport.${type}.${message}`);
  return (
    <Modal
      destroyOnClose
      footer={
        <Space>
          {carrierId && !!resetList && (
            <DeleteButton onClick={handleRemove}>
              {intl('commons.entity.remove')}
            </DeleteButton>
          )}
          <Button onClick={handleCloseModal}>
            {intl('commons.modal.button.cancel')}
          </Button>
          <SaveButton
            onClick={handleOnSave}
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            {intl('commons.entity.save')}
          </SaveButton>
        </Space>
      }
      visible={visible}
      title={translate(carrierId ? 'editCarrier' : 'createCarrier', 'title')}
      onOk={handleOnSave}
      onCancel={handleCloseModal}
    >
      <InvoiceTransportForm
        form={form}
        loading={invoiceCarrier.isLoading}
        address={invoiceCarrier.data?.address}
      />
    </Modal>
  );
}
